// src/data/resumeStore.ts
import { safeGetItem, safeSetItem } from './storage';

export interface SavedFormState {
  code: string; // e.g. MYS-7492
  destination: string; // INDIA, MALAYSIA, VIETNAM
  savedAt: string;
  step: number;
  applicantName?: string;
  formValues: Record<string, any>;
  applicants?: any[];
  photoBase64?: string;
  passportBase64?: string;
}

const STORAGE_PREFIX = 'mySkyResume_';

export const generateRecoveryCode = (): string => {
  const num = Math.floor(1000 + Math.random() * 9000);
  return `MYS-${num}`;
};

export const saveFormProgress = (
  destination: string,
  step: number,
  formValues: Record<string, any>,
  applicants?: any[],
  photoBase64?: string,
  passportBase64?: string,
  existingCode?: string
): SavedFormState => {
  const code = existingCode || generateRecoveryCode();
  const applicantName = formValues.givenName ? `${formValues.givenName} ${formValues.surname || ''}`.trim() : 'Draft Applicant';
  
  const savedState: SavedFormState = {
    code,
    destination,
    savedAt: new Date().toLocaleString(),
    step,
    applicantName,
    formValues,
    applicants,
    photoBase64,
    passportBase64,
  };

  const key = `${STORAGE_PREFIX}${code.trim().toUpperCase()}`;
  safeSetItem(key, JSON.stringify(savedState));

  // Async push to Cloudflare KV for global cross-device access
  fetch('/api/drafts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, data: savedState }),
  }).catch((err) => {
    console.warn('[resumeStore] Cloudflare KV sync error:', err);
  });

  return savedState;
};

// Synchronous local storage lookup
export const loadFormProgress = (code: string): SavedFormState | null => {
  if (!code) return null;
  const rawInput = code.trim().toUpperCase();
  const cleanInput = rawInput.replace(/[^A-Z0-9]/g, '');

  if (!cleanInput) return null;

  try {
    // 1. Direct key match (e.g. mySkyResume_MYS-9227)
    const directKey = `${STORAGE_PREFIX}${rawInput}`;
    const directRaw = safeGetItem(directKey);
    if (directRaw) {
      return JSON.parse(directRaw);
    }

    // 2. Try MYS- prefix if user entered numbers only (e.g. 9227 -> MYS-9227)
    if (/^\d+$/.test(cleanInput)) {
      const prefixKey = `${STORAGE_PREFIX}MYS-${cleanInput}`;
      const prefixRaw = safeGetItem(prefixKey);
      if (prefixRaw) {
        return JSON.parse(prefixRaw);
      }
    }

    // 3. Scan all keys starting with STORAGE_PREFIX for a match ignoring dashes/spaces
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const itemRaw = safeGetItem(key);
        if (itemRaw) {
          try {
            const parsed: SavedFormState = JSON.parse(itemRaw);
            if (parsed && parsed.code) {
              const cleanSavedCode = parsed.code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
              if (cleanSavedCode === cleanInput || cleanSavedCode === `MYS${cleanInput}`) {
                return parsed;
              }
            }
          } catch (e) {
            // Ignore malformed entries
          }
        }
      }
    }
  } catch (err) {
    console.error('Failed to load form progress locally:', err);
  }
  return null;
};

// Async lookup that checks local storage first, then fetches from global Cloudflare KV
export const loadFormProgressAsync = async (code: string): Promise<SavedFormState | null> => {
  const localResult = loadFormProgress(code);
  if (localResult) return localResult;

  if (!code) return null;
  const normalizedCode = code.trim().toUpperCase();

  try {
    const res = await fetch(`/api/drafts?code=${encodeURIComponent(normalizedCode)}`);
    if (res.ok) {
      const json = await res.json();
      if (json && json.success && json.data) {
        // Save to local storage as well for future fast access
        safeSetItem(`${STORAGE_PREFIX}${json.data.code || normalizedCode}`, JSON.stringify(json.data));
        return json.data;
      }
    }
  } catch (err) {
    console.warn('[resumeStore] Cloudflare KV fetch error:', err);
  }

  return null;
};
